import { loadScript } from "./loadScript";
import { logger } from "./loggingUtils";

interface PlotlyConfig {
  data?: { [key: string]: unknown };
  type?: string;
  layout?: {
    [key: string]: unknown;
    autosize: boolean;
    margin?: {
      [key: string]: unknown;
      t?: number;
    };
    title?: string;
  };
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
}

const PLOTLY_BASE = "https://cdn.plot.ly/plotly-basic-2.35.2.min.js";

const ensureValidJson = (rawString: string) => {
  return rawString.replace(/&quot;/g, "\"");
};

/**
 * Takes a string or object from JSON data and parses the title for the Chart if possible.
 * @param title Title to use for the graph.
 * @returns string The parsed title, or an empty string.
 */
const parseTitle = (
  title:
    | string
    | {
        [key: string]: unknown;
        text?: string;
      }
    | undefined
): string => {
  if (!title) return "";

  const parsed = typeof title === "string" ? title : (title?.text ?? "");
  return parsed;
};

const parseGraphData = (
  graphInfo?: string,
  graphConfig?: string,
  graphData?: string,
  graphLayout?: string,
  graphType?: string
) => {
  let config: PlotlyConfig = {};

  if (graphInfo) {
    logger.warn(
      "[Plotly Utils] Graph appears to be using old config, should be updated"
    );

    try {
      config = JSON.parse(ensureValidJson(graphInfo));
    } catch (err) {
      logger.error(
        "[Plotly Utils] Invalid plotly JSON in data-graph-info:",
        err,
        graphInfo
      );
      return config;
    }
  }

  if (graphConfig) {
    try {
      config = JSON.parse(ensureValidJson(graphConfig));
    } catch (err) {
      logger.error("[Plotly Utils] Invalid plotly JSON in data-graph-config:", {
        err,
        graphConfig
      });
    }
  }

  if (graphData) {
    try {
      config.data = JSON.parse(ensureValidJson(graphData));
    } catch (err) {
      logger.error(
        "[Plotly Utils] Invalid plotly JSON in data-graph-data:",
        err,
        graphData
      );
    }
  }

  if (graphLayout) {
    try {
      config.layout = JSON.parse(ensureValidJson(graphLayout));
      if (config?.layout?.title) {
        config.title = parseTitle(config.layout.title);
        config.layout.title = undefined;
      }
    } catch (err) {
      logger.error(
        "[Plotly Utils] Invalid plotly JSON in data-graph-layout:",
        err,
        graphLayout
      );
    }
  }

  if (graphType) {
    config.type = graphType;
  }

  return config;
};

const addTitleElement = (el: HTMLElement, title: string) => {
  const existingTitle = el.previousElementSibling;
  const isTitle = existingTitle?.classList.contains("plotly-title");

  if (isTitle && existingTitle) {
    existingTitle.textContent = title;
  } else {
    const titleElem = document.createElement("p");
    titleElem.className = "plotly-title";
    titleElem.textContent = title;
    el.parentElement?.insertBefore(titleElem, el);
  }
};

const createNewGraphContainer = (
  graphConfig?: string,
  graphData?: string,
  graphLayout?: string,
  graphType?: string
) => {
  const graph = document.createElement("div");
  graph.classList.add("plotly-graph");
  graph.dataset.config = ensureValidJson(graphConfig || "");
  graph.dataset.graphData = ensureValidJson(graphData || "");
  graph.dataset.graphLayout = ensureValidJson(graphLayout || "");
  graph.dataset.graphType = ensureValidJson(graphType || "");
  graph.style.height = "18.75rem";
  graph.style.width = "100%";

  return graph;
};

window.__rendering = window.__rendering || {};

window.__rendering.plotly = {
  ready: false,
  loading: false,
  Plotly: null,

  load: async () => {
    const api = window.__rendering!.plotly!;
    if (api.ready || api.loading) return;
    api.loading = true;

    // If Learnosity/item content already loaded Plotly, just use it.
    if (window.Plotly) {
      api.Plotly = window.Plotly;
      api.ready = true;
      api.loading = false;
      return;
    }

    await loadScript(PLOTLY_BASE);

    api.Plotly = window.Plotly;
    api.ready = true;
    api.loading = false;
  },

  renderElement: (el: HTMLElement) => {
    const api = window.__rendering!.plotly!;
    if (!api.ready || !api.Plotly) return;

    let activeEl = el;
    const { graphInfo, graphConfig, graphData, graphLayout, graphType } =
      activeEl.dataset;
    const config: PlotlyConfig = parseGraphData(
      graphInfo,
      graphConfig,
      graphData,
      graphLayout,
      graphType
    );

    const { data, layout, title, xAxisLabel, yAxisLabel } = config || {};

    if (!data) {
      logger.error("[Plotly Utils] Plotly JSON missing `data` field:", config);
      return;
    }

    // Ensure data is an array of traces
    const traces = Array.isArray(data) ? data : [data];

    // Build a layout that incorporates title / axis labels if provided
    const baseLayout: {
      autosize: boolean;
      width?: undefined;
      xaxis?: { title?: string };
      yaxis?: { title?: string };
      margin: {
        t?: number;
      };
    } = {
      ...layout,
      autosize: true,
      width: undefined,
      margin: {
        t: 0
      }
    };

    // Setup X axis content.
    if (xAxisLabel) {
      baseLayout.xaxis = {
        ...baseLayout.xaxis,
        title: xAxisLabel
      };
    }

    // Setup Y axis content.
    if (yAxisLabel) {
      baseLayout.yaxis = {
        ...baseLayout.yaxis,
        title: yAxisLabel
      };
    }

    const defaultConfig = {
      staticPlot: true, // disable zoom/pan/drag
      responsive: true,
      displayModeBar: false, // hide toolbar
      displaylogo: false, // hide Plotly logo
      // extra safety: kill scroll/double-click interactions even if staticPlot
      scrollZoom: false,
      doubleClick: false,
      dragMode: false
    };

    if (
      graphInfo === undefined &&
      activeEl.classList.contains("graph-editor-placeholder")
    ) {
      // BLD2-609: renderElement can be called twice for the same placeholder
      // (renderAll + MutationObserver). The first call detaches it; `.before`
      // on a detached node throws.
      if (!activeEl.isConnected) return;

      const newGraph = createNewGraphContainer(
        graphConfig,
        graphData,
        graphLayout,
        graphType
      );
      activeEl.before(newGraph);
      activeEl.remove();
      activeEl = newGraph;
    }

    // Render the title outside of the graph.
    if (title) {
      addTitleElement(activeEl, title);
    }

    const isInit = activeEl.dataset.plotlyInitialized === "true";

    const render = async () => {
      const renderFn = isInit ? api.Plotly!.react : api.Plotly!.newPlot;
      const renderResult = renderFn(
        activeEl,
        traces,
        baseLayout,
        defaultConfig
      );

      // Plotly 2.x returns a Promise; but be defensive in case of void
      if ((await renderResult) && typeof renderResult.then === "function") {
        renderResult
          .then(() => {
            activeEl.dataset.plotlyInitialized = "true";
            setTimeout(() => {
              try {
                api.Plotly!.Plots.resize(activeEl);
              } catch (e) {
                logger.warn("[Plotly Utils] Plotly resize failed:", e);
              }
            }, 50);
          })
          .catch((err: unknown) => {
            logger.error("[Plotly Utils] Plotly render error:", err);
          });
      } else {
        // Non-promise fallback
        activeEl.dataset.plotlyInitialized = "true";
        setTimeout(() => {
          try {
            api.Plotly!.Plots.resize(activeEl);
          } catch (e) {
            logger.warn("[Plotly Utils] Plotly resize failed:", e);
          }
        }, 50);
      }
    };

    requestAnimationFrame(() => {
      setTimeout(render, 0);
    });
  },

  renderAll: (container: HTMLElement | string) => {
    const api = window.__rendering!.plotly!;
    if (!api.ready) return;

    const root =
      typeof container === "string"
        ? document.getElementById(container)
        : container;

    if (!root) return;

    // BLD2-609: also handle `.graph-editor-placeholder` — renderElement
    // swaps it for a clean `.plotly-graph`. Needed for first-load when
    // placeholders are in the DOM before usePlotly's MutationObserver
    // attaches.
    const charts = root.querySelectorAll(
      ".plotly-graph, .graph-editor-placeholder"
    );
    for (const chart of charts) {
      api.renderElement(chart as HTMLElement);
    }
  }
};
